import { request } from "@/common/api";
import { Student } from "@/common/types/student";
import { Buttons } from "@/components/atoms/Buttons";
import { InfoList } from "@/components/organisms/InfoList";
import { AlertText } from "@/components/atoms/AlertText";
import { InputGroup } from "@/components/molecules/InputGroup";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { GET_USER_INFO } from "@/common/querykeys";
import { ModalGuardian } from "@/components/containers/ModalContainer/ModalGuardian";
import { useRecoilValue } from "recoil";
import { guardianUserState } from "@/common/states/user";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";
import { Loading } from "@/components/atoms/Loading";

export const SettingGuardianConnectPage = () => {
  const [reloadKey, setReloadKey] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const guardianInfo = useRecoilValue(guardianUserState);

  const { data: student, isLoading } = useQuery<Student>({
    queryKey: [GET_USER_INFO, reloadKey],
    queryFn: async () => {
      return await request<Student>({
        method: "GET",
        url: "/student/info",
      });
    },
  });

  const { mutateAsync: registerGuardian } = useMutation({
    mutationFn: (data: { id: string; guardianId: string }) => {
      return request({
        method: "PUT",
        url: "/student/connect-guardian",
        data: data,
      });
    },
    onSuccess: () => {
      setReloadKey((prev) => prev + 1);
    },
  });

  let userData;
  if (student?.guardian !== null) {
    userData = [
      {
        id: student?.guardian.userId,
        data_name: "법정보호자 연결 정보",
        data: [
          { name: "법정보호자 UID", data: student?.guardian.id },
          { name: "법정보호자 이름", data: student?.guardian.name },
          {
            name: "법정보호자 생년월일",
            data: student?.guardian.birth,
          },
        ],
      },
    ];
  }

  useEffect(() => {
    const connectGuardian = async () => {
      if (
        !guardianInfo?.id ||
        !student?.id ||
        guardianInfo.id === student?.guardian?.id
      ) {
        return;
      }

      try {
        if (guardianInfo && guardianInfo.id && student && student.id) {
          await registerGuardian({
            id: student.id,
            guardianId: guardianInfo.id,
          });
        }
      } catch (error) {
        console.error("@@@ error: ", error);
      }
    };

    connectGuardian();
  }, [student, guardianInfo, registerGuardian]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {student?.guardian === null ? (
            <MainTemplate
              nav={false}
              subPage={true}
              menuBtn={false}
              buttonLeft={
                <Buttons
                  type="button"
                  size="headerTextIcon"
                  layout="text"
                  color="bright100"
                  icon={<IconArrow />}
                  label="설정"
                  onClick={() => window.history.back()}
                />
              }
              buttonRight={
                <Buttons
                  type="button"
                  size="headerText"
                  layout="text"
                  color="bright100"
                  label="연결"
                  onClick={() => setModalShow(true)}
                />
              }
              pageTitle="법정보호자 연결 설정"
            >
              <AlertText
                subText={
                  <>
                    <InputGroup direction="row">
                      <>
                        현재 연결된 법정보호자가 없습니다.
                        <br />
                        우측 상단의 "연결" 및 아래의 "연결하기" 버튼을
                        <br />
                        이용하여 연결이 가능합니다.
                        <Buttons
                          type="submit"
                          size="lg"
                          color="primary100"
                          layout="solid"
                          label="연결하기"
                          onClick={() => setModalShow(true)}
                        />
                      </>
                    </InputGroup>
                  </>
                }
              />
            </MainTemplate>
          ) : (
            <MainTemplate
              nav={false}
              subPage={true}
              menuBtn={false}
              buttonLeft={
                <Buttons
                  type="button"
                  size="headerTextIcon"
                  layout="text"
                  color="bright100"
                  icon={<IconArrow />}
                  label="설정"
                  onClick={() => window.history.back()}
                />
              }
              buttonRight={
                <Buttons
                  type="button"
                  size="headerText"
                  layout="text"
                  color="bright100"
                  label="수정"
                  onClick={() => setModalShow(true)}
                />
              }
              pageTitle="법정보호자 연결 설정"
            >
              <InfoList data={userData} />
            </MainTemplate>
          )}
        </>
      )}
      {modalShow && <ModalGuardian setModalShow={setModalShow} />}
    </>
  );
};
